import { Divider, useTheme } from '@mui/material';
import cn from 'classnames';
import posthog from 'posthog-js';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';

import { ReactComponent as AccountManagerIconActive } from '../../../assets/images/icon-account-manager-active.svg';
import { ReactComponent as AccountManagerIcon } from '../../../assets/images/icon-account-manager.svg';
import { ReactComponent as AlertIcon } from '../../../assets/images/icon-alert.svg';
import { ReactComponent as BudgetSettingsIcon } from '../../../assets/images/icon-budget-settings.svg';
import { ReactComponent as DepartmentIcon } from '../../../assets/images/icon-building.svg';
import { ReactComponent as EnterIcon } from '../../../assets/images/icon-enter.svg';
import { ReactComponent as FeedbackIcon } from '../../../assets/images/icon-feedback.svg';
import { ReactComponent as SignatureIcon } from '../../../assets/images/icon-signature.svg';

import { routes } from '../../../constants/routing';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN, USER } from '../../../constants/users';
import useFeatureBudgetErrors from '../../../hooks/useFeatureBudgetErrors';
import useFirebaseTools from '../../../hooks/useFirebaseTools';
import useModal from '../../../hooks/useModal';
import useNoScroll from '../../../hooks/useNoScroll';
import { toggleUiElement } from '../../../store/actions/shell';
import { selectAdminType, selectUserProfile } from '../../../store/selectors/auth';
import { selectCurrentOrganization, selectIsDepartmentBudgetEnabled } from '../../../store/selectors/organizations';
import { selectIsShowDepartmentBudget, selectIsShowDigitalBudget } from '../../../store/selectors/shell';
import { BUTTON_BUTTON } from '../../../types/forms';
import { DrawerAnimationDirectionEnum } from '../../../types/shell';
import { hasPermission } from '../../../utils/users';
import Drawer from '../../Drawer/Drawer';
import { DepartmentBudget, DigitalBudget, LowInventoryMenuItem, UserInformation } from '../../index';
import { ChangeOrganizationModal } from '../../modals';
import { DisplayCurrencyMenuItem } from '../../_mui';
import { Nav, NavAccordionItem, NavIcon, NavItem, NavText, NavToggle } from '../index';

import styles from './MobileSidenav.module.scss';

interface IProps {
  expanded: boolean;
  toggle: () => void;
  mainMenu: React.ReactNode | React.ReactNode[];
  additionalMenu: React.ReactNode | React.ReactNode[];
  isAuth: boolean;
}

const MobileSidenav = ({ expanded, toggle, mainMenu, additionalMenu, isAuth }: IProps) => {
  const { sass: variables } = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isAccountExpanded, setIsAccountExpanded] = React.useState<boolean>(false);

  const { logout } = useFirebaseTools();
  const {
    isOpen: isOpenChangeOrgModal,
    openModal: openChangeOrgModal,
    closeModal: closeChangeOrgModal,
    Modal: ChangeOrgModal,
  } = useModal();
  const { hasWarnings, setFeatureBudgetWarning } = useFeatureBudgetErrors();

  const auth = useSelector(selectUserProfile);
  const isShowDigitalBudget = useSelector(selectIsShowDigitalBudget);
  const isShowDepartmentBudget = useSelector(selectIsShowDepartmentBudget);
  const adminType = useSelector(selectAdminType);
  const isDepartmentBudgetEnabled = useSelector(selectIsDepartmentBudgetEnabled);
  const currentOrganization = useSelector(selectCurrentOrganization);

  const handleOpenFeedbackSidebar = React.useCallback(() => {
    dispatch(toggleUiElement({ feedbackSidenavIsShown: true }));
  }, [dispatch]);

  const handleLogout = React.useCallback(() => {
    // Need to close sidenav after logout
    if (toggle) {
      toggle();
    }
    posthog.reset();
    logout();
  }, [toggle, logout]);

  const openDepartmentBudgetSidebar = React.useCallback(() => {
    dispatch(toggleUiElement({ drawerIsShown: true }));
  }, [dispatch]);

  const toggleAccountAccordion = React.useCallback(() => {
    setIsAccountExpanded((prev) => !prev);
  }, []);

  const handleChangeOrg = React.useCallback(() => {
    openChangeOrgModal();
    toggle();
  }, [openChangeOrgModal, toggle]);

  const accountManagerItems = React.useMemo(() => {
    return (
      <>
        <DisplayCurrencyMenuItem mobileSideNav />
        {hasPermission([SUPER_ADMIN, DISTRIBUTOR], adminType) ? (
          <NavLink to={routes.signatureSetup} activeClassName={styles.activated}>
            <NavItem className={styles.accountManagerItem} onClick={toggle}>
              <NavIcon>
                <SignatureIcon className={styles.icon} />
              </NavIcon>
              {expanded ? <NavText title="Signature setup" /> : null}
            </NavItem>
          </NavLink>
        ) : null}
        {isDepartmentBudgetEnabled && hasPermission([SUPER_ADMIN, DISTRIBUTOR, ORG_ADMIN], adminType) && (
          <NavItem onClick={openDepartmentBudgetSidebar} className={styles.accountManagerItem}>
            <NavIcon>
              <BudgetSettingsIcon className={styles.icon} />
            </NavIcon>
            {expanded ? <NavText title="Departments management" /> : null}
          </NavItem>
        )}
        {!hasPermission([USER], adminType) && (
          <LowInventoryMenuItem className={cn(styles.lowInventoryBtn, styles.accountManagerItem)} onClick={toggle} />
        )}
        {hasPermission([SUPER_ADMIN, DISTRIBUTOR], adminType) ? (
          <NavItem onClick={handleChangeOrg} className={styles.accountManagerItem}>
            <NavIcon>
              <DepartmentIcon className={styles.icon} />
            </NavIcon>
            {expanded ? <NavText title={currentOrganization?.name || ''} /> : null}
          </NavItem>
        ) : null}
        <Divider className={styles.divider} />
        <NavItem onClick={handleOpenFeedbackSidebar} className={styles.accountManagerItem}>
          <NavIcon>
            <FeedbackIcon className={styles.icon} />
          </NavIcon>
          {expanded ? <NavText title="Leave Feedback" /> : null}
        </NavItem>
        <NavItem onClick={handleLogout} className={styles.accountManagerItem}>
          <NavIcon>
            <EnterIcon className={styles.icon} />
          </NavIcon>
          {expanded ? <NavText title="Log Out" /> : null}
        </NavItem>
      </>
    );
  }, [
    auth?.displayName,
    auth?.photoURL,
    isShowDigitalBudget,
    setFeatureBudgetWarning,
    isDepartmentBudgetEnabled,
    isShowDepartmentBudget,
    adminType,
    toggle,
    currentOrganization?.name,
    isOpenChangeOrgModal,
    openChangeOrgModal,
    openDepartmentBudgetSidebar,
    handleOpenFeedbackSidebar,
    handleLogout,
    expanded,
  ]);

  const additionalInformation = React.useMemo(() => {
    return (
      <div className={styles.top}>
        <UserInformation
          wrapperClassName={styles.userInformation}
          avatarClassName={styles.avatar}
          name={auth?.displayName}
          avatarImageURL={auth?.photoURL}
          organization={currentOrganization?.name}
          isMobile
        />
        <div className={styles.featureBudgetsList}>
          {isShowDigitalBudget && <DigitalBudget onLowBudget={setFeatureBudgetWarning} labelFontSize={12} />}
          {isDepartmentBudgetEnabled && isShowDepartmentBudget && (
            <DepartmentBudget onLowBudget={setFeatureBudgetWarning} />
          )}
        </div>
        <div className={styles.accountManager}>
          <NavAccordionItem
            title="Account"
            icon={AccountManagerIcon}
            activeIcon={AccountManagerIconActive}
            isActive={isAccountExpanded}
            isExpanded={isAccountExpanded}
            onChange={toggleAccountAccordion}
          >
            {accountManagerItems}
          </NavAccordionItem>
        </div>
      </div>
    );
  }, [
    auth?.displayName,
    auth?.photoURL,
    isShowDigitalBudget,
    setFeatureBudgetWarning,
    isDepartmentBudgetEnabled,
    isShowDepartmentBudget,
    adminType,
    toggle,
    currentOrganization?.name,
    isAccountExpanded,
    toggleAccountAccordion,
    accountManagerItems,
  ]);

  const changeOrgModal = React.useMemo(
    () => (
      <ChangeOrgModal className="common-modal">
        {() => {
          return <ChangeOrganizationModal onClose={closeChangeOrgModal} />;
        }}
      </ChangeOrgModal>
    ),
    [closeChangeOrgModal, ChangeOrgModal],
  );

  const handleLogoOnClick = React.useCallback(() => history.push(routes.dashboard), [history]);

  useNoScroll(expanded);

  return (
    <div className={cn(styles.mobileSideNav)}>
      <div className={cn(styles.header)}>
        {isAuth && (
          <div className={styles.burgerWrapper}>
            <div className={styles.burger}>
              <NavToggle expanded={expanded} onClick={toggle} style={{ margin: 1 }} />
              {hasWarnings && <AlertIcon className={styles.alert} />}
            </div>
          </div>
        )}
        <button type={BUTTON_BUTTON} onClick={handleLogoOnClick}>
          <div className={styles.sidebarLogo} />
        </button>
      </div>
      <Drawer
        className={cn(styles.body, { [styles.expanded]: expanded })}
        trigger={expanded}
        direction={DrawerAnimationDirectionEnum.Horizontally}
        from={variables.navMobileMinWidth}
        to={variables.navMobileMaxWidth}
        onClose={toggle}
        withOverlay
      >
        <div className={styles.container}>
          <NavToggle expanded={expanded} onClick={toggle} style={{ margin: 1 }} />
          {additionalInformation}
          <Divider className={styles.divider} />
          <Nav className={styles.mobileNav}>
            {mainMenu}
            <div className={styles.controls}>{additionalMenu}</div>
          </Nav>
        </div>
      </Drawer>
      {changeOrgModal}
    </div>
  );
};

export default MobileSidenav;
